// Sanitiser for stored KB HTML on reader surfaces.
//
// DOMPurify drops every <iframe> by default, which silently emptied every
// PDF/video embed box on the article page (the editor renders the raw doc, so
// the breakage only showed to readers). Iframes are allowed back in, but only
// when their src is an asset we host or a host on the KB embed allow-list —
// the same check the editor applies when an embed is inserted.
import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { hostAllowed } from "@/lib/kbEmbeds";
import { fetchKbSettings } from "@/lib/kbSettings";

const EMBED_ATTRS = ["allow", "allowfullscreen", "frameborder", "loading",
  "height", "width", "title", "referrerpolicy", "sandbox"];

export function sanitizeKbHtml(html, hosts) {
  const clean = DOMPurify.sanitize(html || "", {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: EMBED_ATTRS,
  });
  if (!clean.toLowerCase().includes("<iframe")) return clean;
  // <template> content is inert, so nothing loads while we filter.
  const tpl = document.createElement("template");
  tpl.innerHTML = clean;
  tpl.content.querySelectorAll("iframe").forEach((frame) => {
    if (hostAllowed(frame.getAttribute("src") || "", hosts)) return;
    // Drop the embed wrapper too, or a rejected embed leaves a tall empty box.
    const box = frame.closest(".kb-embed");
    frame.remove();
    if (box && !box.querySelector("iframe")) box.remove();
  });
  return tpl.innerHTML;
}

/** Embed allow-list from KB Settings (session-cached). */
export function useKbEmbedHosts() {
  const [hosts, setHosts] = useState([]);
  useEffect(() => {
    let alive = true;
    fetchKbSettings().then((s) => { if (alive) setHosts(s?.embed_hosts || []); });
    return () => { alive = false; };
  }, []);
  return hosts;
}
